/**
 * Vivier — talent pool listing page.
 *
 * Lists all candidates flagged `in_talent_pool=true`, grouped by reason,
 * with quick actions to reactivate or export as CSV for outbound campaigns.
 */
import { motion } from "framer-motion";
import { Archive, ArrowRight, Download, Filter, Loader2, Phone, Search, Undo2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import SavedViewsBar, { CollapsibleFilters } from "../components/common/SavedViewsBar";
import { api } from "../lib/api";
import { toE164 } from "../lib/format";


function badgeForReason(reason) {
    const map = {
        cant_reach: { label: "Pas joignable", tone: "bg-rose-50 text-rose-700 border-rose-200" },
        not_available: { label: "Pas dispo", tone: "bg-blue-50 text-blue-700 border-blue-200" },
        immersion_noshow: { label: "No-show immersion", tone: "bg-orange-50 text-orange-700 border-orange-200" },
        immersion_cancelled: { label: "Immersion annulée", tone: "bg-purple-50 text-purple-700 border-purple-200" },
        to_recontact_later: { label: "À recontacter plus tard", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
        other: { label: "Autre", tone: "bg-slate-50 text-slate-700 border-slate-200" },
    };
    return map[reason] || map.other;
}


function exportCsv(list) {
    if (!list.length) return;
    const rows = [
        ["Prénom", "Nom", "Email", "Téléphone", "Ville", "Score IA", "Raison", "Au vivier depuis", "Note", "Source"],
        ...list.map((c) => [
            c.first_name || "",
            c.last_name || "",
            c.email || "",
            toE164(c.phone) || c.phone || "",
            c.city || "",
            c.ai_score ?? "",
            badgeForReason(c.talent_pool_reason).label,
            c.talent_pool_at ? new Date(c.talent_pool_at).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris" }) : "",
            (c.talent_pool_note || "").replace(/[\r\n]+/g, " "),
            c.source || "",
        ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `vivier-shariti-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
}


export default function Vivier() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reasonFilter, setReasonFilter] = useState("");
    const [search, setSearch] = useState("");
    const [reactivating, setReactivating] = useState(null);
    const [sortBy, setSortBy] = useState("score"); // "score" | "recent" | "oldest"

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/candidates", { params: { pool: "only" } });
            setList(data || []);
        } catch (_e) {
            setList([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, []);

    const counts = useMemo(() => {
        const c = {};
        for (const cand of list) {
            const k = cand.talent_pool_reason || "other";
            c[k] = (c[k] || 0) + 1;
        }
        return c;
    }, [list]);

    const filtered = useMemo(() => {
        let out = list;
        if (reasonFilter) out = out.filter((c) => c.talent_pool_reason === reasonFilter);
        if (search) {
            const q = search.toLowerCase();
            out = out.filter((c) =>
                `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase().includes(q)
                || (c.email || "").toLowerCase().includes(q)
                || (c.city || "").toLowerCase().includes(q)
                || (c.phone || "").toLowerCase().includes(q)
            );
        }
        // Sort
        return [...out].sort((a, b) => {
            if (sortBy === "recent") {
                return new Date(b.talent_pool_at || 0) - new Date(a.talent_pool_at || 0);
            }
            if (sortBy === "oldest") {
                return new Date(a.talent_pool_at || 0) - new Date(b.talent_pool_at || 0);
            }
            return (b.ai_score || 0) - (a.ai_score || 0);
        });
    }, [list, reasonFilter, search, sortBy]);

    const currentSavedParams = useMemo(() => {
        const p = {};
        if (reasonFilter) p.reason = reasonFilter;
        if (sortBy !== "score") p.sort = sortBy;
        return p;
    }, [reasonFilter, sortBy]);

    function applySavedView(params) {
        setReasonFilter(params?.reason || "");
        setSortBy(params?.sort || "score");
    }

    function viewMatchesCurrent(viewParams) {
        return String(viewParams?.reason || "") === String(currentSavedParams.reason || "")
            && String(viewParams?.sort || "") === String(currentSavedParams.sort || "");
    }

    const filterSummary = useMemo(() => {
        const parts = [];
        if (reasonFilter) parts.push(badgeForReason(reasonFilter).label);
        if (sortBy !== "score") parts.push(sortBy === "recent" ? "Récents" : "Anciens");
        return parts.length ? parts.join(" · ") : "Aucun filtre actif";
    }, [reasonFilter, sortBy]);

    async function reactivate(c) {
        setReactivating(c.id);
        try {
            await api.post(`/candidates/${c.id}/talent-pool/reactivate`);
            toast.success(`${c.first_name} ${c.last_name} a été réactivé(e) ✅`);
            window.dispatchEvent(new CustomEvent("vivier:changed"));
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Action impossible");
        } finally {
            setReactivating(null);
        }
    }

    return (
        <div className="space-y-6" data-testid="vivier-page">
            <header className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 text-amber-800 px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                        <Archive className="h-3 w-3" /> Vivier · talents à recontacter
                    </div>
                    <h1 className="mt-2 text-3xl md:text-4xl font-black tracking-tight text-shariti-black">
                        Vos candidats à recontacter
                    </h1>
                    <p className="mt-1 text-sm font-medium text-slate-500">
                        {list.length} talent{list.length > 1 ? "s" : ""} parké{list.length > 1 ? "s" : ""} — bons profils en attente d'une seconde chance.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => exportCsv(filtered)}
                        disabled={filtered.length === 0}
                        data-testid="vivier-export-csv"
                        className="inline-flex items-center gap-2 bg-shariti-black text-white font-extrabold text-xs uppercase tracking-wider px-4 py-2.5 rounded-full hover:bg-slate-800 disabled:opacity-40 transition"
                    >
                        <Download className="h-3.5 w-3.5" />
                        Exporter CSV ({filtered.length})
                    </button>
                </div>
            </header>

            {/* Saved views */}
            <SavedViewsBar
                scope="vivier"
                currentParams={currentSavedParams}
                onApply={applySavedView}
                activeMatch={viewMatchesCurrent}
            />

            {/* Filters */}
            <CollapsibleFilters storageKey="vivier-filters" summary={filterSummary} defaultOpen>
                <div className="flex flex-wrap items-center gap-2">
                    <Filter className="h-4 w-4 text-slate-400" />
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-400 mr-1">Raison :</span>
                    <button
                        onClick={() => setReasonFilter("")}
                        data-testid="vivier-filter-all"
                        className={`px-3 h-8 rounded-full text-xs font-bold border transition ${
                            !reasonFilter ? "bg-shariti-black text-white border-shariti-black" : "bg-white text-slate-600 border-shariti-gray hover:border-shariti-black"
                        }`}
                    >
                        Toutes <span className="ml-1 opacity-70">{list.length}</span>
                    </button>
                    {["cant_reach", "not_available", "immersion_noshow", "immersion_cancelled", "to_recontact_later", "other"].map((r) => {
                        const meta = badgeForReason(r);
                        const count = counts[r] || 0;
                        if (count === 0 && reasonFilter !== r) return null;
                        return (
                            <button
                                key={r}
                                onClick={() => setReasonFilter(reasonFilter === r ? "" : r)}
                                data-testid={`vivier-filter-${r}`}
                                className={`px-3 h-8 rounded-full text-xs font-bold border transition ${
                                    reasonFilter === r ? "bg-shariti-orange text-white border-shariti-orange" : `${meta.tone} hover:opacity-80`
                                }`}
                            >
                                {meta.label} <span className="ml-1 opacity-70">{count}</span>
                            </button>
                        );
                    })}
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 bg-white border border-shariti-gray rounded-full px-3 h-9 text-sm w-full md:w-72">
                        <Search className="h-4 w-4 text-slate-400" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Nom, email, ville…"
                            data-testid="vivier-search"
                            className="bg-transparent outline-none flex-1 text-shariti-black font-medium"
                        />
                    </div>
                    <div className="ml-auto inline-flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Trier :</span>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            data-testid="vivier-sort"
                            className="h-8 px-3 rounded-full text-xs font-bold border border-shariti-gray bg-white text-slate-700 hover:border-shariti-orange focus:border-shariti-orange focus:outline-none transition"
                        >
                            <option value="score">Meilleur score IA</option>
                            <option value="recent">Mis au vivier récemment</option>
                            <option value="oldest">Mis au vivier le plus ancien</option>
                        </select>
                    </div>
                </div>
            </CollapsibleFilters>

            {/* List */}
            {loading ? (
                <div className="grid gap-3">
                    {[1, 2, 3].map((i) => (<div key={i} className="card-soft h-24 animate-pulse bg-shariti-cream" />))}
                </div>
            ) : filtered.length === 0 ? (
                <div className="card-soft p-12 text-center" data-testid="vivier-empty">
                    <Archive className="h-10 w-10 mx-auto text-slate-300" />
                    <p className="mt-3 font-bold text-slate-500">Aucun talent au vivier pour cette recherche.</p>
                    <p className="text-xs text-slate-400 mt-1">
                        Les candidats que vous mettez au vivier (depuis leur fiche) apparaissent ici.
                    </p>
                </div>
            ) : (
                <div className="grid gap-3" data-testid="vivier-list">
                    {filtered.map((c, i) => {
                        const meta = badgeForReason(c.talent_pool_reason);
                        const pooledOn = c.talent_pool_at ? new Date(c.talent_pool_at).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris", day: "numeric", month: "long", year: "numeric" }) : "—";
                        return (
                            <motion.div
                                key={c.id}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.02 }}
                                className="card-soft p-4 md:p-5 flex flex-wrap items-center gap-4 hover:shadow-lg hover:border-shariti-orange/40 transition cursor-pointer"
                                data-testid={`vivier-row-${c.id}`}
                                onClick={(e) => {
                                    // Don't navigate if user clicked a child action button (call/reactivate)
                                    if (e.target.closest("button, a")) return;
                                    window.location.href = `/candidats/${c.id}`;
                                }}
                            >
                                {c.avatar_url ? (
                                    <img src={c.avatar_url} alt="" className="h-12 w-12 rounded-2xl object-cover shrink-0" />
                                ) : (
                                    <div className="h-12 w-12 rounded-2xl bg-shariti-cream text-shariti-orange font-extrabold flex items-center justify-center shrink-0">
                                        {(c.first_name?.[0] || "?").toUpperCase()}{(c.last_name?.[0] || "").toUpperCase()}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Link to={`/candidats/${c.id}`} className="text-base font-extrabold text-shariti-black hover:text-shariti-orange transition">
                                            {c.first_name} {c.last_name}
                                        </Link>
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${meta.tone}`}>
                                            {meta.label}
                                        </span>
                                        {c.source === "Site Shariti" ? (
                                            <span className="text-[10px] font-bold bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                                                🌐 Site Shariti
                                            </span>
                                        ) : c.source ? (
                                            <span className="text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                                                💼 {c.source.replace("Layan · ", "Layan / ")}
                                            </span>
                                        ) : null}
                                    </div>
                                    <div className="mt-1 text-xs text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-1">
                                        <span>{c.city || "Ville inconnue"}</span>
                                        {c.phone && <span className="font-mono">{toE164(c.phone)}</span>}
                                        {c.email && <span className="truncate">{c.email}</span>}
                                        <span className="text-[11px] font-medium">Au vivier depuis le <strong>{pooledOn}</strong></span>
                                    </div>
                                    {c.talent_pool_note && (
                                        <p className="mt-1.5 text-[11px] italic text-slate-600 line-clamp-2">
                                            “{c.talent_pool_note}”
                                        </p>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 ml-auto">
                                    <div className="text-center">
                                        <div className="text-2xl font-black text-shariti-orange tabular-nums">{c.ai_score || "—"}</div>
                                        <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Score IA</div>
                                    </div>
                                    {c.phone && (
                                        <a
                                            href={`tel:${toE164(c.phone)}`}
                                            data-testid={`vivier-call-${c.id}`}
                                            className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 transition"
                                            title="Appeler"
                                        >
                                            <Phone className="h-4 w-4" />
                                        </a>
                                    )}
                                    <button
                                        onClick={() => reactivate(c)}
                                        disabled={reactivating === c.id}
                                        data-testid={`vivier-reactivate-${c.id}`}
                                        className="inline-flex items-center gap-2 bg-shariti-orange text-white font-extrabold text-xs uppercase tracking-wider px-4 py-2.5 rounded-full hover:bg-[#d66528] disabled:opacity-50 transition shadow-btn"
                                    >
                                        {reactivating === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
                                        Réactiver
                                    </button>
                                    <Link
                                        to={`/candidats/${c.id}`}
                                        data-testid={`vivier-open-${c.id}`}
                                        className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-shariti-cream text-slate-600 hover:text-shariti-orange transition"
                                        title="Ouvrir la fiche"
                                    >
                                        <ArrowRight className="h-4 w-4" />
                                    </Link>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
